'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { formValidation } from '@/lib/formValidation'
import { getReadableImageUrl } from '@/lib/directus'
import { SimpleReCaptcha } from '@/lib/recaptcha/components'

interface HomeContactFooterProps {
	heading?: {
		title: string
	}
	background?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_download: string
			}
		}
	}
}

export const fields = [
	'heading.title',
	'background.alt',
	'background.image_file.image.id',
	'background.image_file.image.filename_download',
]

type FormData = {
	firstName: string
	lastName: string
	phone: string
	email: string
	procedure: string
	timeframe: string
	message: string
	city: string // Honeypot
	marketing: boolean
	recaptchaToken: string
}

export default function HomeContactFooter({
	data,
}: {
	data: HomeContactFooterProps
}) {
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [formError, setFormError] = useState<string>('')
	const [formSuccess, setFormSuccess] = useState<string>('')
	const [recaptchaToken, setRecaptchaToken] = useState<string>('')
	const router = useRouter()

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
	} = useForm<FormData>({
		mode: 'onBlur',
	})

	const handleRecaptchaVerify = (token: string) => {
		setRecaptchaToken(token)
		setValue('recaptchaToken', token)
		setFormError('')
	}

	const handleRecaptchaError = (error: string) => {
		// Не сбрасываем токен при ошибке загрузки, только при ошибке верификации
		if (error.includes('expired') || error.includes('Please complete')) {
			setRecaptchaToken('')
			setValue('recaptchaToken', '')
		}
		setFormError(error)
	}

	const onSubmit = async (data: FormData) => {
		// Check if reCAPTCHA is completed
		if (!recaptchaToken && !data.recaptchaToken) {
			setFormError('Please complete the reCAPTCHA verification')
			return
		}

		setIsSubmitting(true)
		setFormError('')
		setFormSuccess('')

		try {
			const response = await fetch('/api/contact-form-footer/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ...data, recaptchaToken: recaptchaToken || data.recaptchaToken }),
			})

			if (!response.ok) throw new Error()
			// Redirect to thank you page immediately after successful submission
			router.push('/thank-you')
		} catch (error) {
			setFormError('Failed to submit. Please try again.')
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<section className='HomeContactFooter relative overflow-hidden w-full h-[915px] max-lg:h-auto'>
			{/* Background Image */}
			<div className='absolute inset-0 w-full h-full max-lg:relative max-lg:h-[300px]'>
				<div className='relative w-full h-full'>
					<Image
						src={
							data.background?.image_file?.image?.id
								? getReadableImageUrl(
										data.background.image_file.image.id,
										data.background.image_file.image.filename_download
								  )
								: ''
						}
						alt={data.background?.alt || 'Contact Dr. Harmon'}
						fill
						className='object-cover object-right w-full h-full'
						priority
						sizes='(max-width: 768px) 100vw, 100vw'
					/>
				</div>
			</div>

			<div className='container relative'>
				<div className='flex flex-col lg:flex-row h-full'>
					{/* Contact Form Container */}
					<div className='w-full lg:w-1/2 relative z-10 pt-[159px] max-lg:pt-16 max-lg:px-0'>
						<h2 className='font-agopus text-4xl md:text-5xl text-black mb-6 md:mb-12 leading-[56px]'>
							{data.heading?.title || 'Contact Dr. Harmon'}
						</h2>

						<form
							onSubmit={handleSubmit(onSubmit)}
							className='form-contact-footer w-full max-w-[642px]'
						>
							<input type='hidden' {...register('city')} />
							<input type='hidden' {...register('recaptchaToken')} />

							<div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5'>
								{/* First Name */}
								<div className='relative h-[63px]'>
									<input
										{...register('firstName', { required: true })}
										type='text'
										placeholder='First Name *'
										className='w-full h-full pt-8 bg-transparent text-black text-base font-brandon border-b border-black focus:outline-none placeholder:text-black/70'
										aria-required='true'
									/>
								</div>

								{/* Last Name */}
								<div className='relative h-[63px]'>
									<input
										{...register('lastName', { required: true })}
										type='text'
										placeholder='Last Name *'
										className='w-full h-full pt-8 bg-transparent text-black text-base font-brandon border-b border-black focus:outline-none placeholder:text-black/70'
										aria-required='true'
									/>
								</div>

								{/* Phone Number */}
								<div className='relative h-[63px]'>
									<input
										{...register('phone', {
											required: true,
											pattern:
												/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
										})}
										type='tel'
										placeholder='Phone Number *'
										className='w-full h-full pt-8 bg-transparent text-black text-base font-brandon border-b border-black focus:outline-none placeholder:text-black/70'
										aria-required='true'
									/>
								</div>

								{/* Email */}
								<div className='relative h-[63px]'>
									<input
										{...register('email', {
											required: true,
											pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
										})}
										type='email'
										placeholder='E-mail *'
										className='w-full h-full pt-8 bg-transparent text-black text-base font-brandon border-b border-black focus:outline-none placeholder:text-black/70'
										aria-required='true'
									/>
								</div>

								{/* Procedure */}
								<div className='relative h-[63px]'>
									<input
										{...register('procedure', { required: true })}
										type='text'
										placeholder='Procedure *'
										className='w-full h-full pt-8 bg-transparent text-black text-base font-brandon border-b border-black focus:outline-none placeholder:text-black/70'
										aria-required='true'
									/>
								</div>

								{/* Timeframe */}
								<div className='relative h-[63px]'>
									<input
										{...register('timeframe', { required: true })}
										type='text'
										placeholder="What's Your Timeframe? *"
										className='w-full h-full pt-8 bg-transparent text-black text-base font-brandon border-b border-black focus:outline-none placeholder:text-black/70'
										aria-required='true'
									/>
								</div>

								{/* Message */}
								<div className='relative h-[68px] md:col-span-2 mt-4'>
									<input
										{...register('message', { required: true })}
										type='text'
										placeholder='Message *'
										className='w-full h-full pt-8 bg-transparent text-black text-base font-brandon border-b border-black focus:outline-none placeholder:text-black/70'
										aria-required='true'
									/>
								</div>
							</div>

							{/* Marketing Checkbox */}
							<div className='flex flex-col gap-2 mt-8'>
								<label className='flex items-center gap-2'>
									<div className='relative w-[3.125rem] h-[1.125rem] md:w-[1.125rem] md:h-[1.125rem] border border-[#193762]'>
										<input
											type='checkbox'
											{...register('marketing', { required: true })}
											className='absolute w-full h-full cursor-pointer accent-[#193762]'
											aria-required='true'
											aria-invalid={errors.marketing ? 'true' : 'false'}
										/>
									</div>
									<span className='text-black text-base font-brandon leading-normal'>
										I consent to receive phone calls, text messages, and emails
										from Harmon Facial Plastic Surgery.
									</span>
								</label>
								{errors.marketing && (
									<span className='text-red-500 text-sm font-brandon'>
										Please check the consent box to submit the form.
									</span>
								)}
							</div>

							{/* Submit Button */}
							<div className='mt-16 max-lg:mt-12 max-lg:mb-16 md:flex md:justify-between'>
								{/* reCAPTCHA */}
								<SimpleReCaptcha
									onVerify={handleRecaptchaVerify}
									onError={handleRecaptchaError}
									className=''
								/>
								<button
									type='submit'
									disabled={isSubmitting}
									className='w-full md:w-[181px] h-20 bg-brown rounded-[10px] text-white text-2xl font-brandon hover:bg-brown-light transition-colors disabled:opacity-50 flex items-center justify-center max-md:mt-4'
								>
									Submit
								</button>
							</div>

							{/* Form Messages */}
							{formError && (
								<div className='text-red-500 text-sm mt-4'>{formError}</div>
							)}
							{formSuccess && (
								<div className='text-green-500 text-sm mt-4'>{formSuccess}</div>
							)}
						</form>
					</div>
				</div>
			</div>
		</section>
	)
}
